import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

type ToastIcon = 'success' | 'warning' | 'error' | 'info';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly messageService = inject(MessageService);

  // Maps our notify() icon vocabulary onto PrimeNG p-toast severities, which
  // in turn drive the .p-toast-message-<severity> class used for accent
  // colors in styles.scss (see the "PrimeNG Toast Styling" block).
  /** Toast lifetime. Kept in sync with the p-toast-progress animation in styles.scss. */
  static readonly TOAST_MS = 2000;

  /** Window in which an identical error() message is treated as a duplicate of the one on screen. */
  private static readonly ERROR_DEDUPE_MS = 1500;
  private static lastError: { message: string; at: number } = { message: '', at: 0 };

  private static readonly SEVERITY: Record<ToastIcon, 'success' | 'warn' | 'error' | 'info'> = {
    success: 'success',
    warning: 'warn',
    error: 'error',
    info: 'info',
  };

  notify(message: string, icon: ToastIcon = 'success', timer = NotificationService.TOAST_MS): void {
    this.messageService.add({
      key: 'app',
      severity: NotificationService.SEVERITY[icon],
      summary: message,
      life: timer,
    });
  }


  success(message: string, timer = NotificationService.TOAST_MS): void {
    this.notify(message, 'success', timer);
  }

  error(
    message: string,
    title = 'Oops...',
    footer: string | null = null
  ): void {
    // The global errorInterceptor already shows the server message for every failed request;
    // a component that then reports the same message must not pop the same dialog a second time.
    const now = Date.now();
    if (
      message === NotificationService.lastError.message &&
      now - NotificationService.lastError.at < NotificationService.ERROR_DEDUPE_MS &&
      Swal.isVisible()
    ) {
      return;
    }
    NotificationService.lastError = { message, at: now };

    Swal.fire({
      position: 'center',
      icon: 'error',
      title: title,
      text: message,
      ...(footer ? { footer: footer } : {}),
      showConfirmButton: true,
      confirmButtonText: 'OK',
      customClass: {
        popup: 'DangerSweetalert'
      }
    });
  }


  // Toast-based error (red .p-toast-message-error), distinct from error()
  // above which shows a blocking SweetAlert2 modal. Use for failures that
  // shouldn't interrupt the user, e.g. failed login.
  errorToast(message: string, timer = NotificationService.TOAST_MS): void {
    this.notify(message, 'error', timer);
  }

  warning(message: string, timer = NotificationService.TOAST_MS): void {
    this.notify(message, 'warning', timer);
  }


  info(message: string, timer = NotificationService.TOAST_MS): void {
    this.notify(message, 'info', timer);
  }

  custom(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return Swal.fire({
      position: 'top-end',
      showConfirmButton: false,
      timer: NotificationService.TOAST_MS,
      ...options
    });
  }
}
